'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { MultiColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';

interface DedupConfigProps {
  nodeId: string;
  config: { columns?: string[]; keepFirst?: boolean };
}

export function DedupConfig({ nodeId, config }: DedupConfigProps) {
  const { t } = useTranslation();
  const { updateNodeConfig } = useWorkflowStore();
  const [columns, setColumns]     = useState<string[]>(config.columns   || []);
  const [keepFirst, setKeepFirst] = useState(config.keepFirst !== false);
  const inputColumns = useNodeInputColumns(nodeId);

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    if (config.columns) {
      setColumns(config.columns);
    }
    if (typeof config.keepFirst === 'boolean') {
      setKeepFirst(config.keepFirst);
    }
  }, [config.columns, config.keepFirst]);

  const save = (updates: Partial<typeof config>) => {
    updateNodeConfig(nodeId, { type: 'dedup', params: { columns, keepFirst, ...updates } });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.dedup.keyColumns')}</label>
        <MultiColumnSelector values={columns}
          onChange={(cols) => { setColumns(cols); save({ columns: cols }); }}
          columns={inputColumns ?? []}
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">{t('nodes.config.dedup.keep')}</label>
        <div className="flex gap-3">
          {[
            { value: true,  label: t('nodes.config.dedup.first') },
            { value: false, label: t('nodes.config.dedup.last') },
          ].map(({ value, label }) => (
            <button key={String(value)} type="button"
              onClick={() => { setKeepFirst(value); save({ keepFirst: value }); }}
              className={`flex-1 py-2 text-sm font-medium rounded-xl border transition-all ${
                keepFirst === value
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
